import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Calendar,
  Clock,
  MapPin,
  Users,
  Info,
  CheckCircle2,
  AlertCircle,
  X,
  CreditCard,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../shadcn/ui/dialog";
import toastUtils from "../utils/toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../shadcn/ui/select";
import { convertToUTC } from "../utils/timezoneUtils";
import HUpgradeButton from "./HUpgradeButton";
import BookingTypeSelector from "../screens/host-experience/host-calendar/components/BookingTypeSelector";
import { getNormalizedDurationType } from "../utils/bookingTimeframeUtils";
import AddonSelector from "../components/addons/AddonSelector";
import AddonsBreakdown from "../components/addons/AddonsBreakdown";
import * as localService from "../services/localService";
import { FeatureAccess } from "../utils/featureAccessControl";
import logger from "../utils/logger";


/**
 * Unified Booking Modal - handles both creating and modifying bookings
 * @param {boolean} isOpen - Whether modal is open
 * @param {function} onClose - Close handler
 * @param {Date} selectedDate - Selected date for booking
 * @param {object} selectedWorkspace - Pre-selected workspace
 * @param {array} workspaces - List of available workspaces
 * @param {function} onCreateBooking - Handler for creating booking
 * @param {function} onCreateBookingPendingPayment - Handler for creating with payment
 * @param {function} onUpdateBooking - Handler for updating booking
 * @param {array} availableTimeSlots - Available time slots for the date
 * @param {string} timeFormat - Time format (12h or 24h)
 * @param {boolean} hasActiveSubscription - Whether user has active subscription
 * @param {boolean} bookingError - Whether there was a booking error
 * @param {object} existingBooking - Existing booking data (for edit mode)
 * @param {boolean} isEditMode - Whether in edit mode
 * @param {boolean} allowBookingForCustomer - Whether booking is allowed for customers
 * @param {function} onRequestUpgrade - Handler for requesting upgrade
 */
const HBookingModal = ({
  isOpen,
  onClose,
  selectedDate,
  selectedWorkspace,
  workspaces = [],
  onCreateBooking,
  onCreateBookingPendingPayment,
  onUpdateBooking,
  availableTimeSlots = [],
  timeFormat = "12h",
  hasActiveSubscription = false,
  bookingError = false,
  existingBooking = null,
  existingBookings = [],
  isEditMode = false,
  allowBookingForCustomer = true,
  onRequestUpgrade,
}) => {
  // User data for access control
  const currentUser = useMemo(() => localService.getUser(), []);

  // State
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [selectedStartTime, setSelectedStartTime] = useState(null);
  const [selectedEndTime, setSelectedEndTime] = useState(null);
  const [bookingSelection, setBookingSelection] = useState(null); // For BookingTypeSelector
  const [notes, setNotes] = useState("");
  const [attendeeEmails, setAttendeeEmails] = useState([]);
  const [emailInput, setEmailInput] = useState("");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedAddons, setSelectedAddons] = useState([]);

  // Get current workspace
  const currentSpace = selectedSpace || selectedWorkspace;

  // Get all images for the current space
  const getSpaceImages = useCallback(space => {
    if (!space) return [];
    const images = [];

    const pushImage = source => {
      if (!source) return;
      if (typeof source === "string") {
        images.push(source);
      } else if (typeof source === "object") {
        images.push(source?.url || source?.resourceUrl || source?.link);
      }
    };

    if (Array.isArray(space?.media?.images) && space.media.images.length > 0) {
      space.media.images.forEach(pushImage);
    } else if (Array.isArray(space?.images) && space.images.length > 0) {
      space.images.forEach(pushImage);
    } else if (space?.image) {
      pushImage(space.image);
    }

    return images.filter(Boolean);
  }, []);

  const spaceImages = getSpaceImages(currentSpace);
  const hasMultipleImages = spaceImages.length > 1;

  // Auto-slide carousel
  useEffect(() => {
    if (!hasMultipleImages || spaceImages.length <= 1) return;

    const imageCount = spaceImages.length;
    const interval = setInterval(() => {
      setCurrentImageIndex(prev => (prev + 1) % imageCount);
    }, 4000); // Change image every 4 seconds

    return () => clearInterval(interval);
  }, [hasMultipleImages, spaceImages.length]);

  // Reset image index when space changes
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [currentSpace?.id]);

  const goToNext = useCallback(() => {
    setCurrentImageIndex(prev => (prev + 1) % spaceImages.length);
  }, [spaceImages.length]);

  const goToPrev = useCallback(() => {
    setCurrentImageIndex(prev => (prev - 1 + spaceImages.length) % spaceImages.length);
  }, [spaceImages.length]);

  const goToImage = useCallback(index => {
    setCurrentImageIndex(index);
  }, []);

  const requestUpgrade = useCallback(() => {
    if (typeof onRequestUpgrade === "function") {
      onRequestUpgrade();
    }
  }, [onRequestUpgrade]);

  // Format time helper
  const formatTime = useCallback(
    date => {
      if (!date || !(date instanceof Date) || isNaN(date.getTime())) return "—";

      const h = date.getHours();
      const m = date.getMinutes().toString().padStart(2, "0");

      if (timeFormat === "24h") {
        return `${h.toString().padStart(2, "0")}:${m}`;
      } else {
        const period = h >= 12 ? "PM" : "AM";
        const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
        return `${displayHour}:${m} ${period}`;
      }
    },
    [timeFormat]
  );

  // Format date helper
  const formatDate = useCallback(date => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return "Invalid Date";

    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }, []);

  // Reset or populate form when modal opens
  useEffect(() => {
    if (isOpen) {
      if (isEditMode && existingBooking) {
        // Edit mode - populate with existing data
        const bookingStartDate = new Date(existingBooking.startTime);
        const bookingEndDate = new Date(existingBooking.endTime);

        setSelectedSpace(selectedWorkspace);
        setSelectedStartTime(formatTime(bookingStartDate));
        setSelectedEndTime(formatTime(bookingEndDate));
        setBookingSelection(null); // Reset BookingTypeSelector selection
        setNotes(existingBooking.notes || "");
        setAttendeeEmails(existingBooking.emails || []);
        setEmailInput("");
      } else {
        // Create mode - reset form
        setSelectedSpace(selectedWorkspace);
        setSelectedStartTime(null);
        setSelectedEndTime(null);
        setBookingSelection(null); // Reset BookingTypeSelector selection
        setNotes("");
        setAttendeeEmails([]);
        setEmailInput("");
        setSelectedAddons([]);
      }
    }
  }, [isOpen, selectedWorkspace, isEditMode, existingBooking, formatTime]);

  useEffect(() => {
    if (!allowBookingForCustomer) {
      setAttendeeEmails([]);
      setEmailInput("");
    }
  }, [allowBookingForCustomer, isOpen]);

  // Get available time slots as simple array of times
  const getAvailableTimeOptions = () => {
    if (!availableTimeSlots || availableTimeSlots.length === 0) return [];

    const options = availableTimeSlots
      .filter(slot => slot.available !== false)
      .map(slot => ({
        time: formatTime(slot.startTime),
        dateTime: slot.startTime,
        label: formatTime(slot.startTime),
      }));

    // In edit mode, include the current start time even if not in available slots
    if (isEditMode && existingBooking && selectedStartTime) {
      const hasCurrentTime = options.some(opt => opt.time === selectedStartTime);
      if (!hasCurrentTime) {
        options.push({
          time: selectedStartTime,
          dateTime: new Date(existingBooking.startTime),
          label: selectedStartTime,
        });
        options.sort((a, b) => a.dateTime - b.dateTime);
      }
    }

    return options;
  };

  // Get end time options based on selected start time
  const getEndTimeOptions = () => {
    if (!selectedStartTime || !availableTimeSlots || availableTimeSlots.length === 0) return [];

    // Find all slots that come after the selected start time
    const startSlotIndex = availableTimeSlots.findIndex(slot => formatTime(slot.startTime) === selectedStartTime);

    if (startSlotIndex === -1) {
      // If start time not found in slots (edit mode), calculate based on duration
      if (isEditMode && existingBooking) {
        const bookingStartDate = new Date(existingBooking.startTime);
        const bookingEndDate = new Date(existingBooking.endTime);
        return [
          {
            time: formatTime(bookingEndDate),
            dateTime: bookingEndDate,
            label: formatTime(bookingEndDate),
            duration: Math.floor((bookingEndDate - bookingStartDate) / (1000 * 60)),
            durationLabel: formatDuration(Math.floor((bookingEndDate - bookingStartDate) / (1000 * 60))),
          },
        ];
      }
      return [];
    }

    const endOptions = [];
    const minDurationMinutes = (currentSpace?.minimumDuration || 1) * 60;
    const maxDurationMinutes = (currentSpace?.maximumDuration || 24) * 60;

    // Calculate cumulative duration from start time
    let cumulativeDuration = 0;

    for (let i = startSlotIndex; i < availableTimeSlots.length; i++) {
      const slot = availableTimeSlots[i];

      // Add 30 minutes for this slot
      cumulativeDuration += 30;

      // Check if this slot is available
      if (slot.available === false && !isEditMode) break;

      // Check if we've reached minimum duration
      if (cumulativeDuration >= minDurationMinutes && cumulativeDuration <= maxDurationMinutes) {
        endOptions.push({
          time: formatTime(slot.endTime),
          dateTime: slot.endTime,
          label: formatTime(slot.endTime),
          duration: cumulativeDuration,
          durationLabel: formatDuration(cumulativeDuration),
        });
      }

      // Stop if we've reached maximum duration
      if (cumulativeDuration >= maxDurationMinutes) break;
    }

    return endOptions;
  };

  // Format duration helper
  const formatDuration = useCallback(minutes => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }, []);

  // Calculate booking details - use BookingTypeSelector output if available, fallback to old method
  const getBookingDetails = () => {
    // Use BookingTypeSelector output if available (supports all duration types)
    if (bookingSelection && bookingSelection.startTime && bookingSelection.endTime) {
      const durationMs = bookingSelection.endTime.getTime() - bookingSelection.startTime.getTime();
      const durationMinutes = Math.floor(durationMs / (1000 * 60));
      return {
        startTime: bookingSelection.startTime,
        endTime: bookingSelection.endTime,
        duration: durationMinutes,
        durationLabel: formatDuration(durationMinutes),
      };
    }

    // Fallback to old hourly-only method (for backward compatibility)
    if (!selectedStartTime || !selectedEndTime || !currentSpace) return null;

    const startSlot = availableTimeSlots.find(s => formatTime(s.startTime) === selectedStartTime);
    const endSlot = availableTimeSlots.find(s => formatTime(s.endTime) === selectedEndTime);

    let startTime, endTime;

    if (startSlot && endSlot) {
      startTime = startSlot.startTime;
      endTime = endSlot.endTime;
    } else if (isEditMode && existingBooking) {
      // In edit mode, use the booking date but with selected times
      const bookingDate = new Date(existingBooking.startTime);

      // Parse selected times
      const parseTime = timeStr => {
        const match = timeStr.match(/(\d+):(\d+)\s*(AM|PM)?/i);
        if (!match) return null;

        let hours = parseInt(match[1]);
        const minutes = parseInt(match[2]);
        const period = match[3];

        if (period) {
          if (period.toUpperCase() === "PM" && hours !== 12) hours += 12;
          if (period.toUpperCase() === "AM" && hours === 12) hours = 0;
        }

        const date = new Date(bookingDate);
        date.setHours(hours, minutes, 0, 0);
        return date;
      };

      startTime = parseTime(selectedStartTime);
      endTime = parseTime(selectedEndTime);
    } else {
      return null;
    }

    if (!startTime || !endTime) return null;

    const durationMs = endTime.getTime() - startTime.getTime();
    const durationMinutes = Math.floor(durationMs / (1000 * 60));

    return {
      startTime,
      endTime,
      duration: durationMinutes,
      durationLabel: formatDuration(durationMinutes),
    };
  };

  // Handle email management
  const handleAddEmail = () => {
    if (!allowBookingForCustomer) {
      requestUpgrade();
      return;
    }
    const email = emailInput.trim();
    if (!email) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toastUtils.error("Invalid email format");
      return;
    }

    if (attendeeEmails.includes(email)) {
      toastUtils.warning("Email already added");
      return;
    }

    setAttendeeEmails(prev => [...prev, email]);
    setEmailInput("");
  };

  const handleRemoveEmail = emailToRemove => {
    setAttendeeEmails(prev => prev.filter(e => e !== emailToRemove));
  };

  const handleEmailKeyPress = e => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddEmail();
    }
  };

  // Check for conflicts with existing bookings
  const checkBookingConflict = (startTime, endTime) => {
    if (!existingBookings || existingBookings.length === 0 || !currentSpace) {
      return false;
    }

    const spaceBookings = existingBookings.filter(
      booking =>
        (booking.spaceId === currentSpace.id || booking.spaceId?.toString() === currentSpace.id?.toString()) &&
        (!isEditMode || booking.id !== existingBooking?.id)
    );

    if (spaceBookings.length === 0) {
      return false;
    }

    const bufferTimeInMinutes = currentSpace?.bufferTimeInMinutes || 0;
    const bufferMs = bufferTimeInMinutes * 60 * 1000;
    const newStartTime = startTime.getTime();
    const newEndTime = endTime.getTime();

    return spaceBookings.some(booking => {
      const bookingStart = new Date(booking.startTime).getTime();
      const bookingEnd = new Date(booking.endTime).getTime();
      const bookingStartWithBuffer = bookingStart - bufferMs;
      const bookingEndWithBuffer = bookingEnd + bufferMs;

      // Overlap occurs if: new range starts before existing ends (with buffer) AND new range ends after existing starts (with buffer)
      return newStartTime < bookingEndWithBuffer && newEndTime > bookingStartWithBuffer;
    });
  };

  // Handle form submission for creating booking
  const handleSubmitRegularBooking = () => {
    if (!currentSpace) {
      toastUtils.error("Please select a workspace");
      return;
    }

    // Check if using BookingTypeSelector (supports all duration types)
    const hasBookingSelection = bookingSelection && bookingSelection.startTime && bookingSelection.endTime;
    // Fallback to old hourly-only method
    const hasOldSelection = selectedStartTime && selectedEndTime;

    if (!hasBookingSelection && !hasOldSelection) {
      toastUtils.error("Please complete the booking selection");
      return;
    }

    // Check for conflicts if using BookingTypeSelector
    if (hasBookingSelection && bookingSelection.isValid === false) {
      toastUtils.error("Selected time conflicts with an existing booking. Please choose a different time.");
      return;
    }

    const bookingDetails = getBookingDetails();
    if (!bookingDetails) {
      toastUtils.error("Invalid time selection");
      return;
    }

    // Check for conflicts (only for old method, BookingTypeSelector already checks)
    if (!hasBookingSelection && checkBookingConflict(bookingDetails.startTime, bookingDetails.endTime)) {
      toastUtils.error("Selected time conflicts with an existing booking. Please choose a different time.");
      return;
    }

    // Create booking data
    const bookingData = {
      workspaceId: currentSpace.id,
      selectedTimeSlot: {
        startTime: bookingDetails.startTime,
        endTime: bookingDetails.endTime,
        available: true,
      },
      emails: allowBookingForCustomer ? attendeeEmails : [],
      description: notes,
      selectedDate: selectedDate,
      addons: selectedAddons,
    };

    logger.log("📤 Submitting regular booking:", bookingData);
    onCreateBooking(bookingData);
  };

  // Handle form submission for pending payment booking
  const handleSubmitPendingPayment = () => {
    if (!currentSpace) {
      toastUtils.error("Please select a workspace");
      return;
    }

    // Check if using BookingTypeSelector (supports all duration types)
    const hasBookingSelection = bookingSelection && bookingSelection.startTime && bookingSelection.endTime;
    // Fallback to old hourly-only method
    const hasOldSelection = selectedStartTime && selectedEndTime;

    if (!hasBookingSelection && !hasOldSelection) {
      toastUtils.error("Please complete the booking selection");
      return;
    }

    // Check for conflicts if using BookingTypeSelector
    if (hasBookingSelection && bookingSelection.isValid === false) {
      toastUtils.error("Selected time conflicts with an existing booking. Please choose a different time.");
      return;
    }

    const bookingDetails = getBookingDetails();
    if (!bookingDetails) {
      toastUtils.error("Invalid time selection");
      return;
    }

    // Check for conflicts (only for old method, BookingTypeSelector already checks)
    if (!hasBookingSelection && checkBookingConflict(bookingDetails.startTime, bookingDetails.endTime)) {
      toastUtils.error("Selected time conflicts with an existing booking. Please choose a different time.");
      return;
    }

    // Create booking data
    const bookingData = {
      workspaceId: currentSpace.id,
      selectedTimeSlot: {
        startTime: bookingDetails.startTime,
        endTime: bookingDetails.endTime,
        available: true,
      },
      emails: allowBookingForCustomer ? attendeeEmails : [],
      description: notes,
      selectedDate: selectedDate,
      addons: selectedAddons,
    };

    logger.log("📤 Submitting booking with pending payment:", bookingData);
    onCreateBookingPendingPayment(bookingData);
  };

  // Handle form submission for updating booking
  const handleSubmitUpdate = () => {
    if (!existingBooking || !existingBooking.id) {
      toastUtils.error("Booking information is missing");
      return;
    }

    // Check if using BookingTypeSelector (supports all duration types)
    const hasBookingSelection = bookingSelection && bookingSelection.startTime && bookingSelection.endTime;
    // Fallback to old hourly-only method
    const hasOldSelection = selectedStartTime && selectedEndTime;

    if (!hasBookingSelection && !hasOldSelection) {
      toastUtils.error("Please complete the booking selection");
      return;
    }

    // Check for conflicts if using BookingTypeSelector
    if (hasBookingSelection && bookingSelection.isValid === false) {
      toastUtils.error("Selected time conflicts with an existing booking. Please choose a different time.");
      return;
    }

    const bookingDetails = getBookingDetails();
    if (!bookingDetails) {
      toastUtils.error("Invalid time selection");
      return;
    }

    // Check for conflicts (only for old method, BookingTypeSelector already checks)
    if (!hasBookingSelection && checkBookingConflict(bookingDetails.startTime, bookingDetails.endTime)) {
      toastUtils.error("Selected time conflicts with an existing booking. Please choose a different time.");
      return;
    }

    const updatePayload = {
      emails: allowBookingForCustomer ? attendeeEmails : [],
      newStartTime: convertToUTC(bookingDetails.startTime),
      newEndTime: convertToUTC(bookingDetails.endTime),
      notes: notes || undefined,
    };

    logger.log("📤 Updating booking:", updatePayload);
    onUpdateBooking(existingBooking.id, updatePayload);
  };

  // Get available options
  const startTimeOptions = getAvailableTimeOptions();
  const endTimeOptions = getEndTimeOptions();
  const bookingDetails = getBookingDetails();
  // Form is valid only if user has selected date/time data via BookingTypeSelector
  // Require bookingSelection with valid startTime, endTime, and no conflicts
  const isFormValid =
    bookingSelection && bookingSelection.startTime && bookingSelection.endTime && bookingSelection.isValid !== false;

  // Determine modal title and button text
  const modalTitle = isEditMode ? "Modify Booking" : "Create New Booking";
  const modalSubtitle = isEditMode ? "Update your workspace reservation" : "Schedule nnnsa workspace reservation";
  const submitButtonText = isEditMode ? "Update Booking" : "Book";

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-hidden flex flex-col p-0 mx-4 sm:mx-auto">
        {/* Header */}
        <div className="p-4 sm:p-6">
          <div className="mb-6">
            <DialogTitle className="text-xl font-semibold text-gray-900 mb-2">{modalTitle}</DialogTitle>
            <p className="text-sm text-gray-500">{modalSubtitle}</p>
          </div>

          {/* Content */}
          <div className="space-y-6 max-h-[60vh] overflow-y-auto">
            {/* Workspace Info Card - Enhanced (Moved to top) */}
            {currentSpace && (
              <div className="relative overflow-hidden bg-white rounded-2xl border border-gray-200 shadow-sm">
                {/* Space Image Carousel */}
                <div className="relative h-40 bg-gray-100">
                  {spaceImages.length > 0 ? (
                    <>
                      <img
                        src={spaceImages[currentImageIndex]}
                        alt={currentSpace.name}
                        className="w-full h-full object-cover transition-opacity duration-500"
                        onError={e => {
                          e.target.src = "/img/placeholder-workspace.jpg";
                        }}
                      />
                      <div className="absolute inset-0 bg-black/20"></div>

                      {/* Navigation Arrows */}
                      {hasMultipleImages && (
                        <>
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              goToPrev();
                            }}
                            className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-lg transition-all z-10"
                            aria-label="Previous image"
                          >
                            <ChevronLeft className="w-4 h-4 text-gray-700" />
                          </button>
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              goToNext();
                            }}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-lg transition-all z-10"
                            aria-label="Next image"
                          >
                            <ChevronRight className="w-4 h-4 text-gray-700" />
                          </button>
                        </>
                      )}

                      {/* Image Indicators */}
                      {hasMultipleImages && (
                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                          {spaceImages.map((_, index) => (
                            <button
                              key={index}
                              onClick={e => {
                                e.stopPropagation();
                                goToImage(index);
                              }}
                              className={`h-1.5 rounded-full transition-all ${
                                index === currentImageIndex ? "bg-white w-6" : "bg-white/50 w-1.5"
                              }`}
                              aria-label={`Go to image ${index + 1}`}
                            />
                          ))}
                        </div>
                      )}

                      {/* Image Counter */}
                      {hasMultipleImages && (
                        <div className="absolute top-2 right-2 px-2 py-1 bg-black/60 text-white text-xs rounded z-10">
                          {currentImageIndex + 1} / {spaceImages.length}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                      <img
                        src="/img/placeholder-workspace.jpg"
                        alt={currentSpace.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                  {/* Space name overlay on image */}
                  <div className="absolute bottom-0 left-0 right-0 p-4 z-10">
                    <h3 className="text-xl font-bold text-white mb-1 drop-shadow-lg">{currentSpace.name}</h3>
                    <div className="flex items-center text-white/90 text-sm drop-shadow">
                      <MapPin className="w-4 h-4 mr-1" />
                      <span className="line-clamp-1">
                        {typeof currentSpace.location === "string"
                          ? currentSpace.location
                          : currentSpace.location?.name || currentSpace.location?.buildingAddress || ""}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Space Details */}
                <div className="p-4 bg-white">
                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Users className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Capacity</p>
                        <p className="font-semibold text-gray-900">{currentSpace.capacity} people</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <Clock className="w-4 h-4 text-green-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Duration</p>
                        <p className="font-semibold text-gray-900">
                          {currentSpace.minimumDuration || 1}h - {currentSpace.maximumDuration || 24}h
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Pricing */}
                  {currentSpace.pricing && (
                    <div className="pt-3 border-t border-gray-200">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Rate</span>
                        <span className="text-lg font-bold text-indigo-600">{currentSpace.pricing}</span>
                      </div>
                    </div>
                  )}

                  {/* Amenities Preview */}
                  {currentSpace.amenities && currentSpace.amenities.length > 0 && (
                    <div className="pt-3 border-t border-gray-200">
                      <p className="text-xs text-gray-500 mb-2">Amenities</p>
                      <div className="flex flex-wrap gap-1.5">
                        {currentSpace.amenities.slice(0, 6).map((amenity, index) => {
                          const amenityName = typeof amenity === "object" ? amenity.label || amenity.name : amenity;
                          return (
                            <span
                              key={index}
                              className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded-md text-xs font-medium"
                            >
                              {amenityName}
                            </span>
                          );
                        })}
                        {currentSpace.amenities.length > 6 && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-md text-xs">
                            +{currentSpace.amenities.length - 6} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Workspace Selection - Compact (only shown in create mode) */}
            {!isEditMode && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">Change Workspace (Optional)</label>
                <Select
                  value={selectedSpace?.id?.toString() || ""}
                  onValueChange={value => {
                    const space = workspaces.find(w => w.id.toString() === value);
                    setSelectedSpace(space);
                    setSelectedStartTime(null);
                    setSelectedEndTime(null);
                  }}
                >
                  <SelectTrigger className="w-full px-3 py-2 h-auto border rounded-lg text-sm">
                    <SelectValue placeholder="Choose a different workspace..." />
                  </SelectTrigger>
                  <SelectContent>
                    {workspaces.map(workspace => (
                      <SelectItem key={workspace.id} value={workspace.id.toString()}>
                        {workspace.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Booking Selection - Supports all duration types */}
            {currentSpace ? (
              <div>
                <BookingTypeSelector
                  durationType={getNormalizedDurationType(currentSpace)}
                  selectedDate={selectedDate}
                  availableTimeSlots={availableTimeSlots}
                  space={currentSpace}
                  minimumDuration={currentSpace.minimumDuration || 1}
                  maximumDuration={currentSpace.maximumDuration || 24}
                  bufferTimeInMinutes={currentSpace.bufferTimeInMinutes || 0}
                  existingBookings={existingBookings}
                  spaceId={currentSpace.id}
                  timeFormat={timeFormat}
                  onSelectionChange={setBookingSelection}
                  disabled={false}
                />
              </div>
            ) : (
              <div className="p-6 bg-blue-50 border border-blue-200 rounded-xl text-center">
                <Info className="w-12 h-12 text-blue-400 mx-auto mb-3" />
                <p className="text-sm text-blue-700">Please select a workspace to see available booking options.</p>
              </div>
            )}

            {/* Booking Summary */}
            {bookingDetails && (
              <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-blue-900">Booking Details</h3>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Workspace:</span>
                    <span className="font-semibold text-gray-900">{currentSpace.name}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Start:</span>
                    <span className="font-semibold text-gray-900">
                      {bookingSelection?.startTime
                        ? formatDate(bookingSelection.startTime) + " " + formatTime(bookingSelection.startTime)
                        : selectedStartTime || "—"}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">End:</span>
                    <span className="font-semibold text-gray-900">
                      {bookingSelection?.endTime
                        ? formatDate(bookingSelection.endTime) + " " + formatTime(bookingSelection.endTime)
                        : selectedEndTime || "—"}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Duration:</span>
                    <span className="font-semibold text-gray-900">{bookingDetails.durationLabel}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Addon Selector - Creation Mode */}
            {!isEditMode && currentSpace?.addons && currentSpace.addons.length > 0 && (
              <div className="pt-4 border-t border-gray-100">
                <AddonSelector
                  addons={currentSpace.addons}
                  selectedAddons={selectedAddons}
                  onChange={setSelectedAddons}
                  currency={currentSpace.currency || "GHS"}
                />
              </div>
            )}

            {/* Addons Breakdown - View Mode */}
            {isEditMode && existingBooking?.addons && existingBooking.addons.length > 0 && (
              <div className="pt-4 border-t border-gray-100">
                <AddonsBreakdown addons={existingBooking.addons} currency={currentSpace?.currency || "GHS"} />
              </div>
            )}

            {/* Notes */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Notes <span className="text-xs font-normal text-gray-500">(Optional)</span>
              </label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Add any special requirements or notes for this booking..."
                rows={3}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all resize-none"
              />
            </div>

            {/* Attendees */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <Users className="w-4 h-4 inline mr-1" />
                Attendees <span className="text-xs font-normal text-gray-500">(Optional)</span>
              </label>

              {!allowBookingForCustomer && FeatureAccess.canUpgradePlan(currentUser) ? (
                <div className="border border-slate-200 bg-slate-50 text-slate-800 px-4 py-3 rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <p className="text-sm">Sharing bookings with other customers is available on higher plans.</p>
                  <HUpgradeButton size="sm" onClick={requestUpgrade} />
                </div>
              ) : (
                <>
                  <div className="flex gap-2 mb-3">
                    <input
                      type="email"
                      value={emailInput}
                      onChange={e => setEmailInput(e.target.value)}
                      onKeyPress={handleEmailKeyPress}
                      placeholder="Enter email address..."
                      className="flex-1 px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all"
                    />
                    <button
                      type="button"
                      onClick={handleAddEmail}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                    >
                      Add
                    </button>
                  </div>

                  {attendeeEmails.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {attendeeEmails.map((email, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-2 px-3 py-1.5 bg-indigo-100 text-indigo-800 rounded-full text-sm"
                        >
                          <span>{email}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveEmail(email)}
                            className="hover:bg-indigo-200 rounded-full p-0.5 transition-colors"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex flex-col sm:flex-row gap-2 mt-6">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 flex items-center justify-center"
            >
              Cancel
            </button>

            {isEditMode ? (
              // Edit mode - single Update button
              <button
                onClick={handleSubmitUpdate}
                disabled={!isFormValid}
                className={`flex-1 px-4 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 flex items-center justify-center ${
                  isFormValid
                    ? "text-white bg-indigo-600 border border-transparent hover:bg-indigo-700 focus:ring-indigo-500"
                    : "text-gray-500 bg-gray-300 cursor-not-allowed"
                }`}
              >
                {isFormValid ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    {submitButtonText}
                  </>
                ) : (
                  "Complete Form"
                )}
              </button>
            ) : (
              <>
                {/* Show Book button if user has active subscription */}
                {hasActiveSubscription && (
                  <button
                    onClick={handleSubmitRegularBooking}
                    disabled={!isFormValid}
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 flex items-center justify-center ${
                      isFormValid
                        ? "text-white bg-blue-600 border border-transparent hover:bg-blue-700 focus:ring-blue-500"
                        : "text-gray-500 bg-gray-300 cursor-not-allowed"
                    }`}
                  >
                    {isFormValid ? (
                      <>
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Book
                      </>
                    ) : (
                      "Complete Form"
                    )}
                  </button>
                )}

                {/* Show Pay button if: (1) no subscription OR (2) booking failed */}
                {(!hasActiveSubscription || bookingError) && (
                  <button
                    onClick={handleSubmitPendingPayment}
                    disabled={!isFormValid}
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 flex items-center justify-center ${
                      isFormValid
                        ? "text-white bg-green-600 border border-transparent hover:bg-green-700 focus:ring-green-500"
                        : "text-gray-500 bg-gray-300 cursor-not-allowed"
                    }`}
                  >
                    {isFormValid ? (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        Pay
                      </>
                    ) : (
                      "Complete Form"
                    )}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HBookingModal;
